import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Camera, Plus, X, Save, ArrowLeft } from 'lucide-react';
import { useAuth } from '../hooks/useAuth';
import { useUser, useUpdateUserProfile } from '../hooks/useQueries';
import { SocialLink } from '../types';

const platformOptions = [
  { id: 'instagram', name: 'Instagram', icon: '📷', placeholder: 'https://instagram.com/yourusername' },
  { id: 'tiktok', name: 'TikTok', icon: '🎵', placeholder: 'https://tiktok.com/@yourusername' },
  { id: 'shopee', name: 'Shopee', icon: '🛒', placeholder: 'https://shopee.co.id/yourusername' },
  { id: 'facebook', name: 'Facebook', icon: '👥', placeholder: 'https://facebook.com/yourusername' },
  { id: 'x', name: 'X (Twitter)', icon: '🐦', placeholder: 'https://x.com/yourusername' },
  { id: 'threads', name: 'Threads', icon: '🧵', placeholder: 'https://threads.net/@yourusername' },
];

export function ProfileManagePage() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { data: profile, isLoading: loading } = useUser(user?.id || '');
  const updateProfileMutation = useUpdateUserProfile();
  
  const [formData, setFormData] = useState({
    displayName: '',
    bio: '',
    avatar: ''
  });
  
  const [socialLinks, setSocialLinks] = useState<SocialLink[]>([]);

  useEffect(() => {
    if (profile) {
      setFormData({
        displayName: profile.displayName,
        bio: profile.bio,
        avatar: profile.avatar || ''
      });
      setSocialLinks(profile.socialLinks || []);
    }
  }, [profile]);

  const handleSave = async () => {
    if (!user || !profile) return;
    
    try {
      await updateProfileMutation.mutateAsync({
        userId: user.id,
        updates: {
          displayName: formData.displayName,
          bio: formData.bio,
          avatar: formData.avatar,
          socialLinks
        }
      });
      
      alert('Profile updated successfully!');
    } catch (error) {
      console.error('Error updating profile:', error);
      alert('Failed to update profile. Please try again.');
    }
  };

  const addSocialLink = (platform: string) => {
    const platformConfig = platformOptions.find(p => p.id === platform);
    if (!platformConfig) return;

    const newLink: SocialLink = {
      id: `link-${Date.now()}`,
      platform: platform as SocialLink['platform'],
      url: '',
      title: platformConfig.name,
      isActive: true
    };

    setSocialLinks(prev => [...prev, newLink]);
  };

  const updateSocialLink = (id: string, field: keyof SocialLink, value: string | boolean) => {
    setSocialLinks(prev => prev.map(link => 
      link.id === id ? { ...link, [field]: value } : link
    ));
  };

  const removeSocialLink = (id: string) => {
    setSocialLinks(prev => prev.filter(link => link.id !== id));
  };

  const availablePlatforms = platformOptions.filter(
    platform => !socialLinks.some(link => link.platform === platform.id)
  );

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="w-12 h-12 border-4 border-blue-500 border-t-transparent rounded-full animate-spin mx-auto mb-4" />
          <p className="text-gray-600">Loading profile...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white shadow-sm border-b">
        <div className="max-w-2xl mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <button
              onClick={() => navigate('/dashboard')}
              className="flex items-center text-gray-600 hover:text-gray-900"
            >
              <ArrowLeft className="w-4 h-4 mr-2" />
              Back to Dashboard
            </button>
            <h1 className="text-xl font-semibold text-gray-900">Edit Profile</h1>
            <button
              onClick={handleSave}
              disabled={updateProfileMutation.isPending}
              className="bg-gradient-to-r from-blue-600 to-purple-600 text-white px-4 py-2 rounded-xl font-semibold shadow-lg hover:shadow-xl transform hover:scale-105 transition-all duration-200 disabled:opacity-50 disabled:transform-none flex items-center gap-2"
            >
              {updateProfileMutation.isPending ? (
                <>
                  <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                  Saving...
                </>
              ) : (
                <>
                  <Save className="w-4 h-4" />
                  Save
                </>
              )}
            </button>
          </div>
        </div>
      </div>

      <div className="max-w-2xl mx-auto p-4 space-y-6">
        {/* Profile Form */}
        <div className="bg-white rounded-2xl shadow-lg border border-gray-100 p-6 space-y-6">
          <h2 className="text-lg font-semibold text-gray-900">Basic Information</h2>
          
          {/* Avatar */}
          <div className="text-center">
            <div className="relative inline-block">
              {formData.avatar ? (
                <img
                  src={formData.avatar}
                  alt="Profile"
                  className="w-24 h-24 rounded-full object-cover border-4 border-white shadow-lg"
                />
              ) : (
                <div className="w-24 h-24 bg-gray-200 rounded-full flex items-center justify-center border-4 border-white shadow-lg">
                  <Camera className="w-8 h-8 text-gray-400" />
                </div>
              )}
              <button className="absolute bottom-0 right-0 bg-blue-600 text-white p-2 rounded-full shadow-lg hover:bg-blue-700 transition-colors">
                <Camera className="w-4 h-4" />
              </button>
            </div>
            <p className="text-sm text-gray-500 mt-2">Click to change photo</p>
          </div>

          {/* Display Name */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Display Name
            </label>
            <input
              type="text"
              value={formData.displayName}
              onChange={(e) => setFormData(prev => ({ ...prev, displayName: e.target.value }))}
              className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              placeholder="Your name or brand"
            />
          </div>

          {/* Bio */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Bio
            </label>
            <textarea
              value={formData.bio}
              onChange={(e) => setFormData(prev => ({ ...prev, bio: e.target.value }))}
              rows={4}
              className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-none"
              placeholder="Tell people about yourself and what you do..."
            />
            <p className="text-sm text-gray-500 mt-1">
              {formData.bio.length}/200 characters
            </p>
          </div>
        </div>

        {/* Social Links */}
        <div className="bg-white rounded-2xl shadow-lg border border-gray-100 p-6 space-y-6">
          <h2 className="text-lg font-semibold text-gray-900">Social Links</h2>
          
          {/* Existing Links */}
          <div className="space-y-4">
            {socialLinks.map((link) => {
              const platform = platformOptions.find(p => p.id === link.platform);
              return (
                <div key={link.id} className="border border-gray-200 rounded-xl p-4">
                  <div className="flex items-center justify-between mb-3">
                    <div className="flex items-center gap-2">
                      <span className="text-xl">{platform?.icon}</span>
                      <span className="font-medium text-gray-900">{platform?.name}</span>
                    </div>
                    <button
                      onClick={() => removeSocialLink(link.id)}
                      className="text-gray-400 hover:text-red-500 transition-colors"
                    >
                      <X className="w-5 h-5" />
                    </button>
                  </div>
                  
                  <input
                    type="url"
                    value={link.url}
                    onChange={(e) => updateSocialLink(link.id, 'url', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder={platform?.placeholder}
                  />
                  
                  <div className="mt-3">
                    <label className="flex items-center gap-2">
                      <input
                        type="checkbox"
                        checked={link.isActive}
                        onChange={(e) => updateSocialLink(link.id, 'isActive', e.target.checked)}
                        className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                      />
                      <span className="text-sm text-gray-700">Active</span>
                    </label>
                  </div>
                </div>
              );
            })}
          </div>

          {/* Add New Link */}
          {availablePlatforms.length > 0 && (
            <div>
              <p className="text-sm font-medium text-gray-700 mb-3">Add more platforms:</p>
              <div className="grid grid-cols-2 gap-3">
                {availablePlatforms.map(platform => (
                  <button
                    key={platform.id}
                    onClick={() => addSocialLink(platform.id)}
                    className="flex items-center gap-2 p-3 border border-gray-200 rounded-xl hover:border-blue-300 hover:bg-blue-50 transition-colors"
                  >
                    <span className="text-lg">{platform.icon}</span>
                    <span className="font-medium text-gray-700">{platform.name}</span>
                    <Plus className="w-4 h-4 text-gray-400 ml-auto" />
                  </button>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
